import React from "react";
import Carousel from "react-multi-carousel";
import "react-multi-carousel/lib/styles.css";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import ProductBG from "../images/ProductBG.svg";

// Certifications images
import USDACert from "../images/USDACert.svg";
import BioCert from "../images/BioCert.svg";
import IsoCert from "../images/IsoCert.svg";

// Product images
import prod1 from "../images/prod1.png";
import prod2 from "../images/prod2.png";
import prod3 from "../images/prod3.png";

import { useTranslation } from "react-i18next";

const Clients = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const handleProductClick = () => {
    navigate("/products");
  };

  const certifications = [
    { src: USDACert, alt: "USDA" },
    { src: BioCert, alt: "BioCert" },
    { src: IsoCert, alt: "ISO" },
  ];

  const products = [prod1, prod2, prod3];

  const productsResponsive = {
    desktop: {
      breakpoint: { max: 3000, min: 1024 },
      items: 3,
      slidesToSlide: 1,
    },
    tablet: { breakpoint: { max: 1024, min: 768 }, items: 2, slidesToSlide: 1 },
    mobile: { breakpoint: { max: 768, min: 0 }, items: 1, slidesToSlide: 1 },
  };

  const certsResponsive = {
    desktop: {
      breakpoint: { max: 3000, min: 1024 },
      items: 3,
      slidesToSlide: 1,
    },
    tablet: { breakpoint: { max: 1024, min: 768 }, items: 3, slidesToSlide: 1 },
    mobile: { breakpoint: { max: 768, min: 0 }, items: 1, slidesToSlide: 1 },
  };

  return (
    <div className="relative bg-[#fff6e3] py-20 min-h-[220vh] overflow-hidden">
      {/* Background Image */}
      <img
        src={ProductBG}
        alt="Background"
        className="absolute inset-0 w-full h-full object-cover z-0"
      />

      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8 }}
        className="relative z-10 text-center mb-40"
      >
        <h2 className="text-5xl md:text-5xl italic text-[#225601]" style={{ fontFamily: "Nephilm" }}>
          {t("Our Products")}
        </h2>
        <div className="mt-4 w-24 h-1 bg-[#225601] mx-auto rounded-full"></div>
      </motion.div>

      {/* Products Section */}
      <div className="relative z-10 max-w-6xl mx-auto flex flex-col items-center justify-center space-y-12 mb-32 px-4">
<motion.div
  initial={{ opacity: 0, y: -30 }}
  animate={{ opacity: 1, y: 0 }}
  transition={{ duration: 0.8 }}
  className="flex flex-row items-center justify-center space-x-12 flex-wrap"
>
  {products.map((product, index) => {
    const scale = index === 1 ? 1.3 : 0.9;
    return (
      <motion.div
        key={index}
        onClick={handleProductClick}
        whileHover={{
          scale: scale + 0.05,
          boxShadow: "0 20px 40px rgba(0, 0, 0, 0.15)",
        }}
        whileTap={{ scale: scale - 0.05 }}
        initial={{ scale }}
        className="w-68 h-68 sm:w-72 sm:h-75 md:w-84 md:h-84 flex items-center justify-center rounded-xl overflow-hidden cursor-pointer transition-all duration-300 hover:bg-white/50"
      >
        <motion.img
          src={product}
          alt={`Product ${index + 1}`}
          className="max-h-full max-w-full object-contain"
          whileHover={{ scale: 1.05 }}
          transition={{ duration: 0.3 }}
        />
        <motion.div
          className="absolute inset-0 bg-gradient-to-t from-[#225601]/20 to-transparent opacity-0"
          whileHover={{ opacity: 1 }}
          transition={{ duration: 0.3 }}
        />
        <motion.div
          className="absolute bottom-4 left-1/2 transform -translate-x-1/2 bg-[#225601] text-white px-4 py-2 rounded-full text-sm font-medium opacity-0"
          whileHover={{ opacity: 1, y: -5 }}
          transition={{ duration: 0.3 }}
        >
          View Details
        </motion.div>
      </motion.div>
    );
  })}
</motion.div>

      </div>

      {/* Certifications Section */}
      <div className="absolute bottom-0 left-0 w-full z-10 py-12 px-4">
        <div className="container mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="text-center mb-12"
          >
<h2 className="text-5xl md:text-5xl italic text-[#225601]" style={{ fontFamily: "Nephilm" }}>
          {t("Certifications")}
            </h2>
            <div className="mt-3 w-20 h-0.5 bg-gray-800 mx-auto rounded-full"></div>
          </motion.div>

          <Carousel
  responsive={certsResponsive}
  infinite={true}
  autoPlay
  autoPlaySpeed={2500}
  keyBoardControl={true}
  transitionDuration={900}
  removeArrowOnDeviceType={["tablet", "mobile"]}
  renderDotsOutside={false}
  containerClass="w-full max-w-full px-4 sm:px-8" // add px-4 for mobile spacing
  itemClass="px-4 sm:px-2" // more spacing between items on mobile
  rewind={true}
>
  {certifications.map((cert, index) => (
    <motion.div
      key={index}
      className="flex flex-col items-center justify-center"
      whileHover={{ scale: 1.05 }}
      transition={{ type: "spring", stiffness: 250 }}
    >
      <div className="w-28 h-20 sm:w-32 sm:h-24 flex items-center justify-center rounded-lg shadow-md overflow-hidden mx-auto">
        <img
          src={cert.src}
          alt={cert.alt}
          className="max-h-full max-w-full object-contain"
        />
      </div>
    </motion.div>
  ))}
</Carousel>
        </div>
      </div>

      <style>{`
        .react-multi-carousel-list {
          padding: 0;
          margin: 0;
        }
        .react-multi-carousel-track {
          padding: 0;
        }
      `}</style>
    </div>
  );
};

export default Clients;
